/**
 * Template Expression Pipes
 *
 * Provides data transformation operators for template source expressions.
 * Enables complex transformations like filtering, sorting, mapping arrays.
 *
 * @example
 * "{{ company.personnel | where('reportRecipient', true) | sortBy('lastName') | pluck('lastName') | join(', ') }}"
 */

import { formatIsoDateWithTokens, formatMonthYear } from '@/utils/date';

type PipeFunction = (value: unknown, ...args: string[]) => unknown;

/**
 * Registry of available pipe functions
 */
const PIPES: Record<string, PipeFunction> = {
  // Array operations
  where: (value: unknown, field: string, compareValue: string) => {
    if (!Array.isArray(value)) return value;
    const parsedValue = parseValue(compareValue);
    return value.filter((item) => {
      if (typeof item !== 'object' || item === null) return false;
      return (item as Record<string, unknown>)[field] === parsedValue;
    });
  },

  sortBy: (value: unknown, field: string) => {
    if (!Array.isArray(value)) return value;
    return [...value].sort((a, b) => {
      if (typeof a !== 'object' || a === null) return 0;
      if (typeof b !== 'object' || b === null) return 0;
      const aVal = (a as Record<string, unknown>)[field];
      const bVal = (b as Record<string, unknown>)[field];
      if (aVal === undefined) return 1;
      if (bVal === undefined) return -1;
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return aVal.localeCompare(bVal);
      }
      return String(aVal).localeCompare(String(bVal));
    });
  },

  map: (value: unknown, template: string) => {
    if (!Array.isArray(value)) return value;
    return value.map((item) => {
      if (typeof item !== 'object' || item === null) return String(item);
      return template.replace(/\$\{([^}]+)\}/g, (_match, expression) => {
        const trimmed = String(expression).trim();
        if (!trimmed) return '';

        const pipeIndex = trimmed.indexOf('|');
        const path = pipeIndex === -1 ? trimmed : trimmed.substring(0, pipeIndex).trim();
        const pipesExpression = pipeIndex === -1 ? '' : trimmed.substring(pipeIndex + 1).trim();

        let current: unknown = getValueFromItem(item as Record<string, unknown>, path);

        if (pipesExpression) {
          const pipes = parsePipes(pipesExpression);
          current = applyPipes(current, pipes);
        }

        if (current === undefined || current === null) return '';
        return String(current);
      });
    });
  },

  join: (value: unknown, separator: string = ', ') => {
    if (!Array.isArray(value)) return value;
    return value
      .filter((item) => item !== undefined && item !== null && item !== '')
      .join(separator);
  },

  take: (value: unknown, count: string) => {
    if (!Array.isArray(value)) return value;
    const n = parseInt(count, 10);
    return isNaN(n) ? value : value.slice(0, n);
  },

  pluck: (value: unknown, field: string) => {
    if (!Array.isArray(value)) return value;
    return value.map((item) => {
      if (typeof item !== 'object' || item === null) return undefined;
      return (item as Record<string, unknown>)[field];
    });
  },

  // Formatting pipes
  date: (value: unknown, format: string = 'DD/MM/YYYY', _locale: string = 'it-IT') => {
    if (typeof value !== 'string' || value.trim() === '') return '';
    return formatIsoDateWithTokens(value, format);
  },

  bool: (
    value: unknown,
    trueLabel: string = 'Si',
    falseLabel: string = 'No',
    nullLabel: string = 'N.D.'
  ) => {
    if (value === true) return trueLabel;
    if (value === false) return falseLabel;
    return nullLabel;
  },

  number: (value: unknown, locale: string = 'it-IT', options: string = '{}') => {
    const parsedOptions = JSON.parse(options);
    if (typeof value === 'number') {
      if (!Number.isFinite(value)) return '';
      return formatNumber(value, locale, parsedOptions);
    }
    const parsed = parseFloat(String(value));
    if (isNaN(parsed) || !Number.isFinite(parsed)) return '';
    return formatNumber(parsed, locale, parsedOptions);
  },

  bar: (
    value: unknown,
    max: string = '10',
    width: string = '10',
    fill: string = '#',
    empty: string = '-'
  ) => {
    const numeric = typeof value === 'number' ? value : parseFloat(String(value));
    if (!Number.isFinite(numeric)) return '';

    const maxValue = parseFloat(max);
    const widthValue = Math.max(1, parseInt(width, 10) || 10);
    if (!Number.isFinite(maxValue) || maxValue <= 0) {
      return ''.padEnd(widthValue, empty.charAt(0) || '-');
    }

    const fillChar = fill.charAt(0) || '#';
    const emptyChar = empty.charAt(0) || '-';
    const clamped = Math.min(Math.max(numeric, 0), maxValue);
    const filled = Math.round((clamped / maxValue) * widthValue);
    const emptyCount = Math.max(0, widthValue - filled);

    return `${fillChar.repeat(filled)}${emptyChar.repeat(emptyCount)}`;
  },

  monthYear: (value: unknown, locale: string = 'it-IT') => {
    if (typeof value !== 'string') return String(value);
    return formatMonthYear(value, locale);
  },

  link: (value: unknown, url: string) => {
    // For now, just return the value (link formatting happens in renderer)
    // Store metadata for rendering: {text: value, url: url}
    return JSON.stringify({ text: String(value), url });
  },

  wrap: (value: unknown, prefix: string = '', suffix: string = '') => {
    if (value === undefined || value === null) return '';
    const text = String(value);
    if (!text.length) return '';
    return `${prefix}${text}${suffix}`;
  },
};

export function getValueFromItem(item: Record<string, unknown>, path: string): unknown {
  if (!path) return '';
  if (!path.includes('.')) return item[path];
  const segments = path.split('.');
  let current: unknown = item;
  for (const segment of segments) {
    if (current && typeof current === 'object' && segment in current) {
      current = (current as Record<string, unknown>)[segment];
    } else {
      return undefined;
    }
  }
  return current;
}

/**
 * Evaluate a template expression ({{ ... }}) against a context object
 * Returns raw value without string coercion.
 */
export function evaluateTemplateExpression(
  expression: string,
  context: Record<string, unknown>
): unknown {
  if (!expression.trim()) return undefined;

  const match = expression.match(/^\{\{\s*(.+?)\s*\}\}$/);
  const inner = match ? match[1].trim() : expression.trim();

  const pipeIndex = inner.indexOf('|');
  const path = pipeIndex === -1 ? inner : inner.slice(0, pipeIndex).trim();
  const pipesExpression = pipeIndex === -1 ? undefined : inner.slice(pipeIndex + 1).trim();

  let value = getValueFromItem(context, path);

  if (pipesExpression) {
    const pipes = parsePipes(pipesExpression);
    value = applyPipes(value, pipes);
  }

  return value;
}

/**
 * Parse a pipe expression and apply transformations
 *
 * @example
 * parsePipes("where('active', true) | sortBy('name') | join(', ')")
 */
export function parsePipes(expression: string): Array<{
  name: string;
  args: string[];
}> {
  const pipes: Array<{ name: string; args: string[] }> = [];
  const pipeSegments = splitPipeSegments(expression);

  for (const segment of pipeSegments) {
    const match = segment.match(/^(\w+)(?:\((.*)\))?$/);
    if (!match) continue;

    const [, name, argsStr] = match;
    const args = argsStr ? parseArguments(argsStr) : [];
    pipes.push({ name, args });
  }

  return pipes;
}

/**
 * Apply a chain of pipes to a value
 */
export function applyPipes(
  value: unknown,
  pipes: Array<{ name: string; args: string[] }>
): unknown {
  let result = value;

  for (const pipe of pipes) {
    const fn = PIPES[pipe.name];
    if (!fn) {
      console.warn(`Unknown pipe: ${pipe.name}`);
      continue;
    }
    result = fn(result, ...pipe.args);
  }

  return result;
}

/**
 * Parse function arguments from a string
 * Handles quoted strings and simple values
 */
function parseArguments(argsStr: string): string[] {
  const args: string[] = [];
  let current = '';
  let wasQuoted = false;
  let inQuotes = false;
  let quoteChar = '';

  for (let i = 0; i < argsStr.length; i++) {
    const char = argsStr[i];

    // Skip leading whitespace outside quotes
    if (!inQuotes && char === ' ' && !current && !wasQuoted) {
      continue;
    }

    if ((char === "'" || char === '"') && !inQuotes) {
      inQuotes = true;
      wasQuoted = true;
      quoteChar = char;
      continue;
    }

    if (char === quoteChar && inQuotes) {
      inQuotes = false;
      quoteChar = '';
      continue;
    }

    if (char === ',' && !inQuotes) {
      // Only trim if it wasn't quoted
      args.push(wasQuoted ? current : current.trim());
      current = '';
      wasQuoted = false;
      continue;
    }

    current += char;
  }

  if (current || wasQuoted) {
    args.push(wasQuoted ? current : current.trim());
  }

  return args;
}

/**
 * Split a pipe expression into segments while respecting nested quotes/parentheses
 */
function splitPipeSegments(expression: string): string[] {
  const segments: string[] = [];
  let current = '';
  let depth = 0;
  let inQuotes: string | null = null;

  for (let i = 0; i < expression.length; i++) {
    const char = expression[i];
    const prev = i > 0 ? expression[i - 1] : undefined;

    if (inQuotes) {
      current += char;
      if (char === inQuotes && prev !== '\\') {
        inQuotes = null;
      }
      continue;
    }

    if (char === "'" || char === '"') {
      inQuotes = char;
      current += char;
      continue;
    }

    if (char === '(') {
      depth += 1;
      current += char;
      continue;
    }

    if (char === ')') {
      depth = Math.max(0, depth - 1);
      current += char;
      continue;
    }

    if (char === '|' && depth === 0) {
      if (current.trim().length) {
        segments.push(current.trim());
      }
      current = '';
      continue;
    }

    current += char;
  }

  if (current.trim().length) {
    segments.push(current.trim());
  }

  return segments;
}

/**
 * Parse a value string to its appropriate type
 */
function parseValue(str: string): unknown {
  if (str === 'true') return true;
  if (str === 'false') return false;
  if (str === 'null') return null;
  if (str === 'undefined') return undefined;
  const num = Number(str);
  if (!isNaN(num) && str.trim() !== '') return num;
  return str;
}

/**
 * Format a number with locale
 */
function formatNumber(value: number, locale: string, options: Intl.NumberFormatOptions): string {
  return new Intl.NumberFormat(locale, options).format(value);
}
